import { Controller } from "@hotwired/stimulus"

// Types out a rotating set of taglines, one letter at a time, then deletes and moves to the
// next - picked from a shuffled bag so the order feels random without ever repeating a line
// right after itself. Purely decorative (aria-hidden): a plain, static line next to it is what
// screen readers and search engines read as the real heading text, so the animation never
// touches the page's actual content.
export default class extends Controller {
  static targets = ["text"]
  static values = {
    phrases: Array,
    typeSpeed: { type: Number, default: 55 },
    eraseSpeed: { type: Number, default: 28 },
    holdDelay: { type: Number, default: 2200 },
  }

  connect() {
    this.bag = []
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (this.reducedMotion || this.phrasesValue.length < 2) {
      this.textTarget.textContent = this.phrasesValue[0] || ""
      return
    }
    this.reserveHeight()
    this.onResize = () => {
      cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = requestAnimationFrame(() => this.reserveHeight())
    }
    window.addEventListener("resize", this.onResize)
    this.type(this.nextPhrase(), 0)
  }

  disconnect() {
    clearTimeout(this.timer)
    cancelAnimationFrame(this.resizeFrame)
    if (this.onResize) window.removeEventListener("resize", this.onResize)
  }

  // Reserves, as a fixed min-height, the tallest any phrase gets at the current width - so
  // typing or erasing a line that wraps onto more or fewer lines than the others never
  // resizes the headline under the reader's thumb (the page "scrolled itself" on mobile,
  // where long phrases wrap to three lines while short ones wrap to two). Measuring is
  // synchronous, so the browser never paints the in-between phrases this writes and erases.
  reserveHeight() {
    const current = this.textTarget.textContent
    this.element.style.minHeight = ""
    const tallest = this.phrasesValue.reduce((max, phrase) => {
      this.textTarget.textContent = phrase
      return Math.max(max, this.element.offsetHeight)
    }, 0)
    this.textTarget.textContent = current
    this.element.style.minHeight = `${tallest}px`
  }

  // Refills and reshuffles once the bag runs out, so every phrase is shown once per lap.
  nextPhrase() {
    if (this.bag.length === 0) this.bag = this.shuffle([...this.phrasesValue])
    return this.bag.pop()
  }

  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  type(phrase, position) {
    this.textTarget.textContent = phrase.slice(0, position)
    if (position < phrase.length) {
      this.timer = setTimeout(() => this.type(phrase, position + 1), this.typeSpeedValue)
    } else {
      this.timer = setTimeout(() => this.erase(phrase, phrase.length), this.holdDelayValue)
    }
  }

  erase(phrase, position) {
    this.textTarget.textContent = phrase.slice(0, position)
    if (position > 0) {
      this.timer = setTimeout(() => this.erase(phrase, position - 1), this.eraseSpeedValue)
    } else {
      this.timer = setTimeout(() => this.type(this.nextPhrase(), 0), 400)
    }
  }
}
